import styled, { css, keyframes } from 'styled-components';
import { layout, media, colorUtils } from '@prisjakt/ui-base';

import Clickable from '@prisjakt/ui-clickable';
import { TitleSmallText } from '@prisjakt/ui/text/variants';
import CloseIcon from 'mdi-react/CloseIcon';
import SearchIconComponent from 'mdi-react/SearchIcon';

const { spacing050, spacing100 } = layout.measurements;

const BUTTON_MIN = '40px';
const BUTTON_MAX = '135px';

const scaleIn = keyframes`
  from {
    max-width: ${BUTTON_MIN};
  }

  to {
    max-width: ${BUTTON_MAX};
  }
`;

const scaleOut = keyframes`
  from {
    max-width: ${BUTTON_MAX};
  }

  to {
    max-width: ${BUTTON_MIN};
  }
`;

export const SearchButton = styled.button<{
  disabled: boolean;
}>(
  p => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background-color: ${p.theme.colors.yellove500};
    height: 100%;
    padding: 0 ${spacing100};
    user-select: none;
    cursor: ${p.disabled ? 'initial' : 'pointer'};
    width: ${BUTTON_MIN};
    min-width: ${BUTTON_MIN};
    transition: background-color 150ms ease;
    overflow: hidden;

    &:hover {
      background-color: ${colorUtils.shadeColor(
        p.theme.colors.yellove500,
        p.disabled ? 0 : -10
      )};
    }

    ${media.mediumDown`
      padding: 0 ${spacing050};
    `}

    ${media.smallOnly`
      background-color: ${
        p.disabled ? 'transparent' : p.theme.colors.yellove500
      };
    `};

    ${media.mediumUp`
      ${p.disabled &&
        css`
          animation: ${scaleOut} 150ms;
          width: ${BUTTON_MIN};
        `}

      ${!p.disabled &&
        css`
          max-width: ${BUTTON_MAX};
          width: auto;
          animation: ${scaleIn} 150ms;
        `}
    `};
  `
);

export const StyledSearchBar = styled.div<{
  $focused: boolean;
  $active: boolean;
}>(
  p => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${layout.searchBar.height.desktop.rem};
    overflow: hidden;
    position: relative;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    background: ${p.theme.colors.base0};

    ${media.smallOnly`
      box-shadow: none;
    `};

    ${SearchButton} {
      ${media.smallOnly`
        border-radius: ${p.$active ? '4px' : 0};
      `};
    }
  `
);

export const InputWrapper = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  height: 100%;
  opacity: 1;

  ${media.smallOnly`
    flex: 1 1 auto;
  `};
`;

export const SearchInput = styled.input`
  background: transparent;
  border: none;
  width: 100%;
  height: ${layout.searchBar.height.desktop.rem};
  flex-grow: 1;
  padding: 0 ${spacing100};

  ${media.smallOnly`
    height: 48px;
  `};

  &:focus {
    outline: none;
  }
`;

export const ClearIcon = styled(CloseIcon)``;

type ClearButtonProps = {
  show?: boolean;
};

export const ClearButton = styled(Clickable)<ClearButtonProps>`
  display: ${p => (p.show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.bg.tertiary};
  color: ${p => p.theme.icons.primary};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  margin-right: ${spacing100};
  cursor: pointer;
  transition: background-color 150ms ease;
  flex: 0 0 auto;

  &:hover {
    background-color: ${p => colorUtils.shadeColor(p.theme.bg.tertiary, -20)};
  }
`;

export const SearchIcon = styled(SearchIconComponent)``;

export const SearchButtonText = styled(TitleSmallText)`
  > * {
    align-items: center;
    justify-content: center;
  }
`;

export const Text = styled.span<{ $visible: boolean }>`
  display: ${p => (p.$visible ? 'inline' : 'none')};

  ${media.smallOnly`
    display: none;
  `};
`;
